// Shared value formatting for the panel.
//
// One module so a number means the same thing on every page — the old panel
// had `formatDuration` copy-pasted into two pages with different rounding,
// which is exactly how "median session" comes to read differently in Growth
// than it does on a user's own page.

// Stat-tile value formatting — auto-compact per the dataviz skill's stat
// tile contract: 1,284 / 12.9K / 4.2M. Below 10,000 a comma-separated
// integer reads faster than a compacted one; above it, compacting keeps
// the tile from growing wider than its label.
export function formatCompactNumber(n) {
  if (n == null || Number.isNaN(n)) return '-';
  const abs = Math.abs(n);
  if (abs < 10000) return n.toLocaleString('en-US');
  if (abs < 1_000_000) return `${(n / 1000).toFixed(1)}K`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}

/** Percentages arrive from the backend already rounded; null means "unknown",
 *  which is not the same fact as zero and must not render as "0%". */
export function formatPct(v, digits = 0) {
  if (v == null || Number.isNaN(v)) return '-';
  return `${Number(v).toFixed(digits)}%`;
}

/** Seconds → "42s" / "3m 12s" / "1h 04m". */
export function formatDuration(seconds) {
  if (seconds == null || Number.isNaN(seconds)) return '-';
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, '0')}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${String(m % 60).padStart(2, '0')}m`;
}

/** Milliseconds → "840ms" / "2.4s". Latency is read in ms until it isn't. */
export function formatMs(ms) {
  if (ms == null || Number.isNaN(ms)) return '-';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

/** USD, with enough precision to be useful at per-turn scale. */
export function formatUsd(v) {
  if (v == null || Number.isNaN(v)) return '-';
  const abs = Math.abs(v);
  if (abs === 0) return '$0';
  if (abs < 0.01) return `$${v.toFixed(4)}`;
  if (abs < 1) return `$${v.toFixed(3)}`;
  if (abs < 1000) return `$${v.toFixed(2)}`;
  return `$${Math.round(v).toLocaleString('en-US')}`;
}

export function formatDateTime(iso) {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

export function formatDate(iso) {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** "4m ago" / "3h ago" / "2d ago" — for job lists and audit rows, where the
 *  question is always "how stale is this", never "what wall-clock time". */
export function formatRelative(iso) {
  if (!iso) return '-';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '-';
  const secs = Math.round((Date.now() - then) / 1000);
  if (secs < 0) return 'just now';
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(iso);
}

/** Turns a raw enum/key into something a human reads: `pending_review` →
 *  "Pending review", `mobile_web` → "Mobile web". */
export function humanize(key) {
  if (!key) return '-';
  const s = String(key).replace(/[._-]+/g, ' ').trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}
